import { createSvgEl } from '@learnforge/demo-render';
import type { WaveAnnotations } from '../trig/waves';

export interface AnnotationLayout {
  /** Visible x range — envelope and midline span it. */
  xDomain: [number, number];
  /** CSS colour of the annotated function (envelope lines). */
  color: string;
  /** 1/zoom — offsets, caps and arrowheads multiply by it to stay screen-stable. */
  chromeScale: number;
}

export interface WaveAnnotationsHandle {
  group: SVGGElement;
  update(a: WaveAnnotations | null, layout: AnnotationLayout): void;
  destroy(): void;
}

/**
 * On-chart parameter annotations for one wave: dotted ±A envelope, faint midline,
 * amplitude bracket (A), period bracket (T), phase arrow (C), shift arrow (D).
 * Rebuilt on every update; lives in the y-up world group, so labels counter-flip.
 */
export function createWaveAnnotations(parent: SVGGElement): WaveAnnotationsHandle {
  const group = createSvgEl('g', { class: 'tg-annot' });
  parent.appendChild(group);

  function line(cls: string, x1: number, y1: number, x2: number, y2: number): void {
    group.appendChild(
      createSvgEl('line', { class: cls, x1, y1, x2, y2, 'vector-effect': 'non-scaling-stroke' }),
    );
  }
  function label(text: string, x: number, y: number, anchor: 'start' | 'middle' | 'end'): void {
    const t = createSvgEl('text', {
      class: 'tg-annot-label',
      x,
      y: -y,
      transform: 'scale(1 -1)',
      'text-anchor': anchor,
      'dominant-baseline': 'middle',
    });
    t.textContent = text;
    group.appendChild(t);
  }
  /** Arrowhead at (tipX, tipY) pointing along (dx, dy) ∈ {±1, 0} axes. */
  function arrowhead(tipX: number, tipY: number, dx: number, dy: number, size: number): void {
    const bx = tipX - dx * size;
    const by = tipY - dy * size;
    const px = dy * size * 0.6; // perpendicular
    const py = dx * size * 0.6;
    group.appendChild(
      createSvgEl('path', {
        class: 'tg-annot-line',
        d: `M ${bx + px} ${by + py} L ${tipX} ${tipY} L ${bx - px} ${by - py}`,
        'vector-effect': 'non-scaling-stroke',
      }),
    );
  }

  return {
    group,
    update(a, layout) {
      group.replaceChildren();
      if (!a) {
        group.style.display = 'none';
        return;
      }
      group.style.display = '';
      group.style.setProperty('--annot-color', layout.color);
      const cs = layout.chromeScale;
      const [x0, x1] = layout.xDomain;
      const cap = 0.12 * cs;
      const gap = 0.35 * cs;
      const pad = 0.18 * cs;

      if (a.envelope) {
        for (const y of a.envelope) line('tg-annot-envelope', x0, y, x1, y);
      }
      if (a.midline !== 0) line('tg-annot-midline', x0, a.midline, x1, a.midline);

      if (a.amplitude) {
        const { x, y0, y1 } = a.amplitude;
        line('tg-annot-line', x, y0, x, y1);
        line('tg-annot-line', x - cap, y1, x + cap, y1);
        label('A', x + pad, (y0 + y1) / 2, 'start');
      }

      if (Number.isFinite(a.period.x1)) {
        const y = (a.envelope ? a.envelope[1] : a.midline) + gap;
        line('tg-annot-line', a.period.x0, y, a.period.x1, y);
        line('tg-annot-line', a.period.x0, y - cap, a.period.x0, y + cap);
        line('tg-annot-line', a.period.x1, y - cap, a.period.x1, y + cap);
        label(a.period.label, (a.period.x0 + a.period.x1) / 2, y + pad, 'middle');
      }

      if (a.phase) {
        const dir = a.phase.x1 > 0 ? 1 : -1;
        line('tg-annot-line', a.phase.x0, a.phase.y, a.phase.x1, a.phase.y);
        arrowhead(a.phase.x1, a.phase.y, dir, 0, cap);
        // Away from the x-axis: above the arrow when D ≥ 0, below when D < 0.
        label('C', a.phase.x1 / 2, a.phase.y + (a.phase.y < 0 ? -pad : pad), 'middle');
      }

      if (a.shift) {
        const dir = a.shift.y1 > 0 ? 1 : -1;
        line('tg-annot-line', a.shift.x, a.shift.y0, a.shift.x, a.shift.y1);
        arrowhead(a.shift.x, a.shift.y1, 0, dir, cap);
        // Away from the y-axis: right of the arrow when C ≥ 0, left when C < 0.
        const side = a.shift.x < 0 ? -1 : 1;
        label('D', a.shift.x + side * pad, a.shift.y1 / 2, side < 0 ? 'end' : 'start');
      }
    },
    destroy() {
      group.remove();
    },
  };
}
